/**
 * CloudAlertsPage — V2 real-time nurse call alerts page for Cloud 5.0 platform.
 *
 * 4 tabs: Emergency (real-time), Batteries (60s poll), Communication Lost (60s poll), Other (60s poll).
 * Sound notifications for new emergency alarms. Station Mode (fullscreen).
 * NotificationContext integration for real-time cloud emergency alerts.
 *
 * Key differences from 4.0 AlertsPage:
 *   - Uses `activeCloudAlarms` (not `activeAlarms`) from NotificationContext
 *   - Snake_case data schema (event_id, event_datetime_utc, event_category, etc.)
 *   - Maintenance data from `getdevicemaintenance` stored procedure (all 3 non-emergency tabs)
 *   - No Clear button on maintenance alerts
 *   - No role-based group filtering on emergency alerts
 *   - Emergency table has Description, Accepted By/Time, Device Cleared columns
 *
 * Props: fullscreen — boolean for station mode (initial value)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useSound from 'use-sound';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import { useNotifications } from '../../../contexts/NotificationContext.jsx';
import {
    getSession, getCampusId, getCampusList, updateSessionCampus,
    getUserId, getUsername,
} from '../../../services/session.js';
import { sortFunc, setSoundsMute } from '../../../libs.jsx';
import { fetchCloudMaintenance, acceptAlert, finishAlert } from './api/cloudAlertsApi.js';
import { fetchReasonCodes, fetchHighPriorityEntities } from '../../settings/system-settings/api/cloudSettingsApi.js';

import CloudEmergencyTable from './components/CloudEmergencyTable.jsx';
import CloudMaintenanceTable from './components/CloudMaintenanceTable.jsx';
import AlertSyncIndicator from './components/AlertSyncIndicator.jsx';
import StationModeInfoTip from './components/StationModeInfoTip.jsx';
import MobileModeInfoTip from './components/MobileModeInfoTip.jsx';
import FinishReasonModal from './components/FinishReasonModal.jsx';
import MobileAlertsView from '../../../components/mobile-alerts/MobileAlertsView.jsx';
import MobileReasonDrawer from '../../../components/mobile-alerts/MobileReasonDrawer.jsx';
import emergSound from '../../../sound/emergency_tone.mp3';

import './alerts-page.css';

const CloudAlertsPage = ({ fullscreen: fullscreenProp }) => {
    // ======================================================================
    // SESSION
    // ======================================================================
    // Held in state (not useMemo) so writes from other pages — e.g. saving
    // portal_afr in CloudSystemSettingsPage — propagate via the
    // `session:updated` event without requiring a full reload.
    const [session, setSessionState] = useState(() => getSession());
    useEffect(() => {
        const handler = () => setSessionState(getSession());
        window.addEventListener('session:updated', handler);
        return () => window.removeEventListener('session:updated', handler);
    }, []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);
    const isCloud = Boolean(campusConfig?.is_cloud);
    const portalAfr = Boolean(campusConfig?.portal_afr);

    // ======================================================================
    // NOTIFICATION CONTEXT (real-time cloud emergency alerts)
    // ======================================================================
    const {
        activeCloudAlarms, isConnected, connectionError, isDataStale,
        registerAlertViewer, unregisterAlertViewer, resubscribeToCurrentCampus,
    } = useNotifications();

    useEffect(() => {
        registerAlertViewer();
        return () => unregisterAlertViewer();
    }, [registerAlertViewer, unregisterAlertViewer]);

    // ======================================================================
    // STATE
    // ======================================================================
    const [activeTab, setActiveTab] = useState('emergency');
    const [fullscreen, setFullscreen] = useState(Boolean(fullscreenProp));
    const [mobileView, setMobileView] = useState(false);
    const [lowBatData, setLowBatData] = useState([]);
    const [lostData, setLostData] = useState([]);
    const [otherData, setOtherData] = useState([]);

    // Sort state
    const [emergSort, setEmergSort] = useState({ col: 'event_datetime_utc', dir: 'asc' });
    const [maintSort, setMaintSort] = useState({ col: 'event_datetime_utc', dir: 'asc' });

    // Sound
    const [emergencySound] = useSound(emergSound, { volume: 0.5 });
    const [soundOff, setSoundOff] = useState(session?.soundsOff || false);
    const alertKeyListRef = useRef([]);

    // High-priority event types (config) — Set of event_type_id (as strings) flagged
    // high priority for this campus. Loaded once per campus; used to pin + highlight.
    const [highPriorityTypeIds, setHighPriorityTypeIds] = useState(() => new Set());

    // Portal Accept/Finish
    const [reasonCodes, setReasonCodes] = useState([]);
    const [pendingEventId, setPendingEventId] = useState(null); // eventId with an in-flight accept/finish
    const [finishTarget, setFinishTarget] = useState(null);     // alarm row awaiting reason selection

    // Toast
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 6000);
    }, []);
    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // ======================================================================
    // EMERGENCY ALERTS (from cloud context — no group filtering for 5.0)
    // ======================================================================
    const filteredEmergency = useMemo(() => {
        return [...(activeCloudAlarms || [])];
    }, [activeCloudAlarms]);

    // ======================================================================
    // SOUND — play for new alarm IDs
    // ======================================================================
    useEffect(() => {
        let playSound = false;
        const currentKeys = alertKeyListRef.current;
        filteredEmergency.forEach((alarm) => {
            if (!currentKeys.includes(alarm.event_id)) {
                if (!soundOff) playSound = true;
                currentKeys.push(alarm.event_id);
            }
        });
        alertKeyListRef.current = currentKeys;
        if (playSound) emergencySound();
    }, [filteredEmergency, soundOff, emergencySound]);

    // ======================================================================
    // SORTED DATA
    // ======================================================================
    // High-priority alarms always pin to the top; the active sort then orders
    // rows within the high-priority and normal groups independently.
    const isHighPriority = useCallback(
        (alarm) => highPriorityTypeIds.has((alarm?.event_type_id ?? '').toString()),
        [highPriorityTypeIds],
    );

    const sortedEmergency = useMemo(() => {
        const sorted = [...filteredEmergency];
        sorted.sort((a, b) => {
            const ap = isHighPriority(a) ? 1 : 0;
            const bp = isHighPriority(b) ? 1 : 0;
            if (ap !== bp) return bp - ap;
            return sortFunc(a, b, emergSort.col, emergSort.dir);
        });
        return sorted;
    }, [filteredEmergency, emergSort, isHighPriority]);

    const sortedLowBat = useMemo(() => {
        const sorted = [...lowBatData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [lowBatData, maintSort]);

    const sortedLost = useMemo(() => {
        const sorted = [...lostData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [lostData, maintSort]);

    const sortedOther = useMemo(() => {
        const sorted = [...otherData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [otherData, maintSort]);

    // ======================================================================
    // POLLING — Maintenance (60s) — all 3 non-emergency tabs from same endpoint
    // ======================================================================
    const loadMaintenance = useCallback(async () => {
        if (!campusId) return;
        try {
            const data = await fetchCloudMaintenance(campusId);
            const tmpBat = [];
            const tmpLost = [];
            const tmpOther = [];
            (Array.isArray(data) ? data : []).forEach((item) => {
                const cond = (item.condition || '').toLowerCase();
                if (['low battery', 'missing battery'].includes(cond)) {
                    tmpBat.push(item);
                } else if (cond === 'communication lost') {
                    tmpLost.push(item);
                } else {
                    tmpOther.push(item);
                }
            });
            setLowBatData(tmpBat);
            setLostData(tmpLost);
            setOtherData(tmpOther);
        } catch (err) {
            console.error('[CloudAlertsPage] loadMaintenance error:', err);
        }
    }, [campusId]);

    useEffect(() => { loadMaintenance(); }, [loadMaintenance]);

    useEffect(() => {
        const timer = setInterval(loadMaintenance, 60000);
        return () => clearInterval(timer);
    }, [loadMaintenance]);

    // ======================================================================
    // HIGH-PRIORITY EVENT TYPES — config data, loaded once per campus.
    // Not real-time: it only changes when an admin edits High Priority settings,
    // so we fetch on mount + campus change (mirrors the reason-codes pattern) and
    // keep a Set of event_type_id for O(1) lookups while alarms stream in.
    // ======================================================================
    useEffect(() => {
        if (!campusId) {
            setHighPriorityTypeIds(new Set());
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchHighPriorityEntities(campusId);
                if (cancelled) return;
                const ids = (Array.isArray(data) ? data : [])
                    .map((r) => (r.event_type_id ?? '').toString())
                    .filter((v) => v !== '' && v !== 'null' && v !== 'undefined');
                setHighPriorityTypeIds(new Set(ids));
            } catch (err) {
                console.error('[CloudAlertsPage] fetchHighPriorityEntities error:', err);
                if (!cancelled) setHighPriorityTypeIds(new Set());
            }
        })();
        return () => { cancelled = true; };
    }, [campusId]);

    // ======================================================================
    // REASON CODES — loaded once per campus while portal_afr is enabled
    // ======================================================================
    useEffect(() => {
        if (!portalAfr || !campusId) {
            setReasonCodes([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchReasonCodes(campusId);
                if (!cancelled) setReasonCodes(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('[CloudAlertsPage] fetchReasonCodes error:', err);
                if (!cancelled) setReasonCodes([]);
            }
        })();
        return () => { cancelled = true; };
    }, [portalAfr, campusId]);

    // ======================================================================
    // ACCEPT / FINISH HANDLERS
    // ======================================================================
    const handleAccept = useCallback(async (row) => {
        if (!row?.event_id || pendingEventId) return;
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await acceptAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                userId: getUserId(),
                username: getUsername(),
                displayName: session?.display || getUsername(),
            });
            if (ok) {
                showToast('success', 'Alert accepted.');
                // AppSync subscription will repaint the row naturally.
            } else {
                showToast('error', errMsg || 'Failed to accept alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, session, showToast]);

    const handleOpenFinish = useCallback((row) => {
        if (!row?.event_id || pendingEventId) return;
        setFinishTarget(row);
    }, [pendingEventId]);

    const handleCancelFinish = useCallback(() => {
        setFinishTarget(null);
    }, []);

    const handleSubmitFinish = useCallback(async (reasonId, residentId) => {
        const row = finishTarget;
        if (!row?.event_id || !reasonId) return;
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await finishAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                reasonId,
                // single resident → implicit; multiple → user-selected; none → 0
                residentId: residentId ?? row.residents?.[0]?.resident_id ?? 0,
            });
            if (ok) {
                setFinishTarget(null);
                showToast('success', 'Alert finished.');
            } else {
                showToast('error', errMsg || 'Failed to finish alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [finishTarget, campusId, showToast]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        const cfg = session?.campusData?.[val.toString()];
        // Cloud → on-premise redirect
        if (!cfg?.is_cloud && isCloud) {
            updateSessionCampus(val);
            window.location.href = '/alerts';
            return;
        }
        // On-premise → cloud redirect
        if (cfg?.is_cloud && !isCloud) {
            updateSessionCampus(val);
            window.location.href = '/emergency';
            return;
        }
        setCampusId(val);
        updateSessionCampus(val);
        resubscribeToCurrentCampus();
    }, [session, isCloud, resubscribeToCurrentCampus]);

    const handleSoundToggle = useCallback((e) => {
        const muted = e.target.checked;
        setSoundOff(muted);
        if (session) setSoundsMute(session, muted);
    }, [session]);

    const handleEmergSort = useCallback((col) => {
        setEmergSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleMaintSort = useCallback((col) => {
        setMaintSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            {!fullscreen ? <NavigationV2 /> : null}
            <div className={fullscreen ? '' : 'nav2-main'}>
                <div className={`na-page${fullscreen ? ' na-page--fullscreen' : ''}`}>
                    {/* Toast */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`} role="status" aria-live="polite">
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="dp-toast__close">{'\u2715'}</button>
                        </div>
                    ) : null}

                    {/* Finish Reason Modal — table view only.
                        In mobile view, the drawer renders inside the mobile container so it
                        anchors to that area instead of the viewport. */}
                    {finishTarget && !mobileView ? (
                        <FinishReasonModal
                            row={finishTarget}
                            reasonCodes={reasonCodes}
                            saving={pendingEventId === finishTarget.event_id}
                            onSubmit={handleSubmitFinish}
                            onCancel={handleCancelFinish}
                        />
                    ) : null}

                    {/* Header */}
                    <div className="na-header">
                        <div className="na-header__left">
                            <h1 className="na-header__title">Emergency Alerts</h1>
                            <AlertSyncIndicator isConnected={isConnected} isDataStale={isDataStale} connectionError={connectionError} />
                        </div>
                        <div className="na-header__right">
                            <label className="na-sound-toggle">
                                <input type="checkbox" checked={soundOff} onChange={handleSoundToggle} />
                                <span>Mute Sound</span>
                            </label>
                            {!fullscreen ? (
                                <>
                                    {activeTab === 'emergency' ? (
                                        <MobileModeInfoTip>
                                            <button
                                                className={`na-mobile-toggle rp-btn rp-btn--${mobileView ? 'primary' : 'secondary'}${mobileView ? ' na-mobile-toggle--always' : ''}`}
                                                onClick={() => setMobileView((v) => !v)}
                                            >
                                                <i className="fe fe-smartphone" style={{ marginRight: 4 }} />
                                                {mobileView ? 'Exit Mobile' : 'Mobile Mode'}
                                            </button>
                                        </MobileModeInfoTip>
                                    ) : null}
                                    {mobileView ? null : (
                                        <StationModeInfoTip>
                                            <button className="na-station-toggle rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(true)}>
                                                <i className="fe fe-maximize" style={{ marginRight: 4 }} />Station Mode
                                            </button>
                                        </StationModeInfoTip>
                                    )}
                                    {mobileView ? null : (
                                        <div className="dp-campus-row">
                                            <label htmlFor="ca-campus" className="dp-campus-label">Campus:</label>
                                            {campusList.length > 1 ? (
                                                <select id="ca-campus" className="dp-campus-select" value={campusId} onChange={handleCampusChange}>
                                                    {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                                </select>
                                            ) : (
                                                <span className="dp-campus-name">{campusConfig?.name || ''}</span>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <button className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(false)}>
                                    <i className="fe fe-minimize" style={{ marginRight: 4 }} />Exit Station Mode
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tab Bar — hidden in mobile mode so the user must exit to switch tabs. */}
                    {mobileView ? null : (
                    <div className="na-tab-bar">
                        <button className={`na-tab${activeTab === 'emergency' ? ' is-active' : ''}`} onClick={() => setActiveTab('emergency')}>
                            Emergency
                            {filteredEmergency.length > 0 ? <span className="na-badge na-badge--danger">{filteredEmergency.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'batteries' ? ' is-active' : ''}`} onClick={() => setActiveTab('batteries')}>
                            Batteries
                            {lowBatData.length > 0 ? <span className="na-badge na-badge--info">{lowBatData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'lost' ? ' is-active' : ''}`} onClick={() => setActiveTab('lost')}>
                            Communication Lost
                            {lostData.length > 0 ? <span className="na-badge na-badge--info">{lostData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'other' ? ' is-active' : ''}`} onClick={() => setActiveTab('other')}>
                            Other
                            {otherData.length > 0 ? <span className="na-badge na-badge--info">{otherData.length}</span> : null}
                        </button>
                    </div>
                    )}

                    {/* Content */}
                    <div className={`na-content${mobileView ? ' na-content--mobile' : ''}`}>
                        {activeTab === 'emergency' && mobileView ? (
                            <div className="na-mobile-wrap">
                                <MobileAlertsView
                                    alerts={filteredEmergency}
                                    sessionUserId={getUserId()}
                                    sessionUsername={getUsername()}
                                    onAccept={portalAfr ? handleAccept : undefined}
                                    onFinish={portalAfr ? handleOpenFinish : undefined}
                                    pendingEventId={pendingEventId}
                                    highPriorityTypeIds={highPriorityTypeIds}
                                />
                                {finishTarget ? (
                                    <MobileReasonDrawer
                                        open
                                        row={finishTarget}
                                        reasonCodes={reasonCodes}
                                        saving={pendingEventId === finishTarget.event_id}
                                        onSubmit={handleSubmitFinish}
                                        onCancel={handleCancelFinish}
                                    />
                                ) : null}
                            </div>
                        ) : null}

                        {activeTab === 'emergency' && !mobileView ? (
                            <CloudEmergencyTable
                                data={sortedEmergency}
                                sortColumn={emergSort.col}
                                sortOrder={emergSort.dir}
                                onSort={handleEmergSort}
                                canInteract={portalAfr}
                                pendingEventId={pendingEventId}
                                onAccept={handleAccept}
                                onFinish={handleOpenFinish}
                                highPriorityTypeIds={highPriorityTypeIds}
                            />
                        ) : null}

                        {activeTab === 'batteries' ? (
                            <CloudMaintenanceTable data={sortedLowBat} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}

                        {activeTab === 'lost' ? (
                            <CloudMaintenanceTable data={sortedLost} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}

                        {activeTab === 'other' ? (
                            <CloudMaintenanceTable data={sortedOther} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CloudAlertsPage;
